import React from 'react';
import { Box, Typography, Paper, Grid } from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import LoadingSpinner from '../../../common/LoadingSpinner';
import { DynamicIcon } from '../../../Permissions/Roles/utils/iconMap';
import { TRACKING_MODES } from '../../../../constants/trackingModes';

// Default color scheme when no color is provided
const DEFAULT_COLORS = { bg: '#f3f4f6', border: '#d1d5db', icon: '#6b7280' };

// Helper to generate color variants from a base color
const getColorVariants = (baseColor) => {
  if (!baseColor) return DEFAULT_COLORS;
  return {
    bg: `${baseColor}15`, // 15% opacity for background
    border: `${baseColor}50`, // 50% opacity for border
    icon: baseColor,
  };
};

const SelectTrackingMethod = ({
  measurementTypes,
  selectedType,
  onSelect,
  isLoading,
}) => {
  if (isLoading) {
    return <LoadingSpinner message="Loading tracking methods..." />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Select Tracking Method
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Choose how you want to track progress for this development plan.
      </Typography>

      <Grid container spacing={2}>
        {measurementTypes.map((type) => {
          const colors = getColorVariants(type.color);
          const isSelected = selectedType?.id === type.id;

          return (
            <Grid size={{ xs: 12, sm: 6 }} key={type.id} sx={{ display: 'flex' }}>
              <Paper
                elevation={0}
                onClick={() => onSelect(type)}
                sx={{
                  p: 3,
                  width: '100%',
                  cursor: 'pointer',
                  border: '2px solid',
                  borderColor: isSelected ? colors.icon : colors.border,
                  backgroundColor: isSelected ? colors.bg : 'white',
                  borderRadius: 2,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: colors.icon,
                    backgroundColor: colors.bg,
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    mb: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: 2,
                      backgroundColor: colors.bg,
                      border: `1px solid ${colors.border}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: colors.icon,
                    }}
                  >
                    {type.icon ? (
                      <DynamicIcon iconName={type.icon} sx={{ fontSize: 32 }} />
                    ) : (
                      <BlockIcon sx={{ fontSize: 32 }} />
                    )}
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight={600}>
                      {type.name}
                    </Typography>
                  </Box>
                </Box>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mb: 1,
                    whiteSpace: 'pre-line',
                  }}
                >
                  {type.description}
                </Typography>

                {type.usageGuideline && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mt: 1,
                      p: 1,
                      backgroundColor: 'rgba(0,0,0,0.03)',
                      borderRadius: 1,
                      fontStyle: 'italic',
                      whiteSpace: 'pre-line',
                    }}
                  >
                    {type.usageGuideline}
                  </Typography>
                )}

              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default SelectTrackingMethod;
